/**
 * WHAT: Notification system management UI for templates, recipients, routing, and history.
 * WHY: DAMA sends automated notifications for attestation deadlines, campaign events,
 *      and compliance alerts -- this component lets administrators configure notification
 *      types, email templates, recipient groups, and routing rules.
 */

import React, { useState, useEffect, useCallback } from 'react';
import API_CONFIG from '../config/api.config';
import Swal from 'sweetalert2';
import { WideModal } from './common';

/**
 * WHAT: Full-featured notification management component with dashboard, templates,
 *       recipient groups, routing rules, and notification history tabs.
 * WHY: Enables administrators to control how and when DAMA communicates with users
 *      about attestation tasks, compliance deadlines, and system events.
 */
const NotificationManager = () => {
  // Active tab
  const [activeTab, setActiveTab] = useState('dashboard');

  // Dashboard data
  const [dashboardStats, setDashboardStats] = useState(null);
  const [loadingDashboard, setLoadingDashboard] = useState(true);

  // Notification types
  const [notificationTypes, setNotificationTypes] = useState([]);
  const [typeCategories, setTypeCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('');

  // Templates
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [showTemplateModal, setShowTemplateModal] = useState(false);

  // Recipient groups
  const [recipientGroups, setRecipientGroups] = useState([]);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [showGroupModal, setShowGroupModal] = useState(false);
  const [editingGroup, setEditingGroup] = useState(false);
  const [groupForm, setGroupForm] = useState({
    group_code: '',
    group_name: '',
    description: '',
    technology_code: '',
    members: [],
    include_roles: []
  });
  const [newEmail, setNewEmail] = useState('');

  // Routing rules
  const [routingRules, setRoutingRules] = useState([]);
  const [showRoutingModal, setShowRoutingModal] = useState(false);
  const [routingForm, setRoutingForm] = useState({
    type_code: '',
    technology_code: '',
    group_id: '',
    is_cc: false,
    priority: 0
  });

  // Template editing
  const [editingTemplate, setEditingTemplate] = useState(false);
  const [templateForm, setTemplateForm] = useState({
    type_code: '',
    technology_code: '',
    template_name: '',
    description: '',
    subject_template: '',
    body_template_html: '',
    body_template_text: '',
    is_default: false
  });

  // Settings editing
  const [editingSetting, setEditingSetting] = useState(null);

  // Technologies
  const [technologies, setTechnologies] = useState([]);

  // History
  const [history, setHistory] = useState([]);
  const [historyStats, setHistoryStats] = useState({});
  const [historyDays, setHistoryDays] = useState(7);

  // Queue
  const [queue, setQueue] = useState([]);
  const [queueStats, setQueueStats] = useState({});

  // Settings
  const [settings, setSettings] = useState([]);

  // Test notification
  const [testForm, setTestForm] = useState({
    type_code: '',
    technology_code: '',
    recipient_email: ''
  });
  const [sendingTest, setSendingTest] = useState(false);

  // Loading states
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  /* WHAT: Fetches aggregate notification stats (sent, pending, failed counts).
   * WHY: The dashboard tab provides administrators an at-a-glance health check
   *      of the notification pipeline so issues are caught early. */
  const loadDashboardStats = useCallback(async () => {
    setLoadingDashboard(true);
    try {
      const response = await fetch(API_CONFIG.getDatabasesEndpoint('notifications/dashboard/'));
      const data = await response.json();
      if (data.success) {
        setDashboardStats(data);
      }
    } catch (err) {
      console.error('Error loading dashboard:', err);
    } finally {
      setLoadingDashboard(false);
    }
  }, []);

  /* WHAT: Loads the catalogue of notification types, optionally filtered by category.
   * WHY: Admins need to see and toggle which event types (e.g., campaign_started,
   *      attestation_due) are enabled before routing or templating them. */
  const loadNotificationTypes = useCallback(async () => {
    try {
      const url = selectedCategory
        ? `notifications/types/?category=${selectedCategory}`
        : 'notifications/types/';
      const response = await fetch(API_CONFIG.getDatabasesEndpoint(url));
      const data = await response.json();
      if (data.success) {
        setNotificationTypes(data.types);
        setTypeCategories(data.categories);
      }
    } catch (err) {
      console.error('Error loading types:', err);
    }
  }, [selectedCategory]);

  /* WHAT: Fetches all email templates from the backend.
   * WHY: The Templates tab displays and manages the HTML/text templates used
   *      for each notification type so admins can customise wording and branding. */
  const loadTemplates = useCallback(async () => {
    try {
      const response = await fetch(API_CONFIG.getDatabasesEndpoint('notifications/templates/'));
      const data = await response.json();
      if (data.success) {
        setTemplates(data.templates);
      }
    } catch (err) {
      console.error('Error loading templates:', err);
    }
  }, []);

  /* WHAT: Loads all recipient groups (named sets of email addresses).
   * WHY: Routing rules target groups rather than individual emails, so admins
   *      must be able to browse and manage group membership. */
  const loadRecipientGroups = useCallback(async () => {
    try {
      const response = await fetch(API_CONFIG.getDatabasesEndpoint('notifications/groups/'));
      const data = await response.json();
      if (data.success) {
        setRecipientGroups(data.groups);
      }
    } catch (err) {
      console.error('Error loading groups:', err);
    }
  }, []);

  /* WHAT: Fetches all routing rules that map notification types to recipient groups.
   * WHY: Routing rules determine who receives each notification type; admins need
   *      to view and modify these to ensure the right people get the right alerts. */
  const loadRoutingRules = useCallback(async () => {
    try {
      const response = await fetch(API_CONFIG.getDatabasesEndpoint('notifications/routing/'));
      const data = await response.json();
      if (data.success) {
        setRoutingRules(data.rules);
      }
    } catch (err) {
      console.error('Error loading routing rules:', err);
    }
  }, []);

  /* WHAT: Loads available database technology codes (Oracle, MSSQL, etc.).
   * WHY: Routing and templates can be scoped to a specific technology; this list
   *      populates the technology dropdown in the create/edit forms. */
  const loadTechnologies = useCallback(async () => {
    try {
      const response = await fetch(API_CONFIG.getDatabasesEndpoint('notifications/technologies/'));
      const data = await response.json();
      if (data.success) {
        setTechnologies(data.technologies);
      }
    } catch (err) {
      console.error('Error loading technologies:', err);
    }
  }, []);

  /* WHAT: Fetches recent notification send history filtered by a configurable day range.
   * WHY: The History tab lets admins audit which notifications were sent, to whom,
   *      and whether delivery succeeded or failed. */
  const loadHistory = useCallback(async () => {
    try {
      const response = await fetch(API_CONFIG.getDatabasesEndpoint(`notifications/history/?days=${historyDays}`));
      const data = await response.json();
      if (data.success) {
        setHistory(data.history);
        setHistoryStats(data.stats);
      }
    } catch (err) {
      console.error('Error loading history:', err);
    }
  }, [historyDays]);

  /* WHAT: Loads the current notification send queue and queue statistics.
   * WHY: Queued notifications await processing; admins can monitor backlog
   *      and manually trigger processing when needed. */
  const loadQueue = useCallback(async () => {
    try {
      const response = await fetch(API_CONFIG.getDatabasesEndpoint('notifications/queue/'));
      const data = await response.json();
      if (data.success) {
        setQueue(data.queue);
        setQueueStats(data.stats);
      }
    } catch (err) {
      console.error('Error loading queue:', err);
    }
  }, []);

  /* WHAT: Fetches global notification settings (e.g., sender address, throttle limits).
   * WHY: Settings control system-wide notification behaviour and are editable
   *      from the Settings tab so admins don't need backend access. */
  const loadSettings = useCallback(async () => {
    try {
      const response = await fetch(API_CONFIG.getDatabasesEndpoint('notifications/settings/'));
      const data = await response.json();
      if (data.success) {
        setSettings(data.settings);
      }
    } catch (err) {
      console.error('Error loading settings:', err);
    }
  }, []);

  // Initial load
  useEffect(() => {
    loadDashboardStats();
    loadNotificationTypes();
    loadTechnologies();
  }, [loadDashboardStats, loadNotificationTypes, loadTechnologies]);

  // Load tab-specific data
  useEffect(() => {
    if (activeTab === 'templates') loadTemplates();
    if (activeTab === 'groups') loadRecipientGroups();
    if (activeTab === 'routing') loadRoutingRules();
    if (activeTab === 'history') loadHistory();
    if (activeTab === 'queue') loadQueue();
    if (activeTab === 'settings') loadSettings();
  }, [activeTab, loadTemplates, loadRecipientGroups, loadRoutingRules, loadHistory, loadQueue, loadSettings]);

  // Reload types when category changes
  useEffect(() => {
    loadNotificationTypes();
  }, [selectedCategory, loadNotificationTypes]);

  /* WHAT: Triggers server-side processing of all pending queued notifications.
   * WHY: Provides a manual "flush" button so admins can force-send queued notifications
   *      without waiting for the next scheduled processing cycle. */
  const handleProcessQueue = async () => {
    setLoading(true);
    try {
      const response = await fetch(API_CONFIG.getDatabasesEndpoint('notifications/queue/process/'), {
        method: 'POST'
      });
      const data = await response.json();
      if (data.success) {
        Swal.fire({
          icon: 'success',
          title: 'Queue Processed',
          html: `
            <p>Processed: ${data.processed}</p>
            <p>Sent: ${data.sent}</p>
            <p>Failed: ${data.failed}</p>
          `,
          timer: 3000
        });
        loadQueue();
        loadDashboardStats();
      } else {
        Swal.fire('Error', data.error || 'Failed to process queue', 'error');
      }
    } catch (err) {
      Swal.fire('Error', err.message, 'error');
    } finally {
      setLoading(false);
    }
  };

  /* WHAT: Sends a one-off test notification to a specified email address.
   * WHY: Allows admins to verify that templates render correctly and email
   *      delivery works before enabling a notification type for production use. */
  const handleSendTest = async (e) => {
    e.preventDefault();
    if (!testForm.type_code || !testForm.recipient_email) {
      Swal.fire('Error', 'Please select a notification type and enter recipient email', 'error');
      return;
    }

    setSendingTest(true);
    try {
      const response = await fetch(API_CONFIG.getDatabasesEndpoint('notifications/test/'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(testForm)
      });
      const data = await response.json();
      if (data.success) {
        Swal.fire({
          icon: 'success',
          title: 'Test Sent',
          text: `Notification sent to ${testForm.recipient_email}`,
          timer: 3000
        });
        loadHistory();
      } else {
        Swal.fire('Error', data.error || 'Failed to send test', 'error');
      }
    } catch (err) {
      Swal.fire('Error', err.message, 'error');
    } finally {
      setSendingTest(false);
    }
  };

  /* WHAT: Toggles a notification type between enabled and disabled.
   * WHY: Admins may need to temporarily silence certain notification types
   *      (e.g., during maintenance) without deleting the configuration. */
  const handleToggleType = async (typeCode, currentEnabled) => {
    try {
      const response = await fetch(API_CONFIG.getDatabasesEndpoint(`notifications/types/${typeCode}/`), {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ default_enabled: !currentEnabled })
      });
      const data = await response.json();
      if (data.success) {
        loadNotificationTypes();
      } else {
        Swal.fire('Error', data.error, 'error');
      }
    } catch (err) {
      Swal.fire('Error', err.message, 'error');
    }
  };

  // ============================================
  // RECIPIENT GROUP CRUD
  // ============================================

  /* WHAT: Opens the recipient group create/edit modal, pre-populating form data for edits.
   * WHY: Reuses one modal for both create and edit flows, reducing duplicate UI code
   *      while ensuring the form is always initialised to the correct state. */
  const openGroupModal = (group = null) => {
    if (group) {
      setEditingGroup(true);
      setSelectedGroup(group);
      // Handle members that might be JSON string or array
      const members = Array.isArray(group.members) ? group.members :
        (typeof group.members === 'string' ? JSON.parse(group.members || '[]') : []);
      const includeRoles = Array.isArray(group.include_roles) ? group.include_roles :
        (typeof group.include_roles === 'string' ? JSON.parse(group.include_roles || '[]') : []);
      setGroupForm({
        group_code: group.group_code,
        group_name: group.group_name,
        description: group.description || '',
        technology_code: group.technology_code || '',
        members: members,
        include_roles: includeRoles
      });
    } else {
      setEditingGroup(false);
      setSelectedGroup(null);
      setGroupForm({
        group_code: '',
        group_name: '',
        description: '',
        technology_code: '',
        members: [],
        include_roles: []
      });
    }
    setNewEmail('');
    setShowGroupModal(true);
  };

  /* WHAT: Validates and appends a new email address to the recipient group's member list.
   * WHY: Client-side email validation prevents invalid addresses from reaching the
   *      backend and guards against duplicate entries within the same group. */
  const handleAddEmail = () => {
    const email = newEmail.trim().toLowerCase();
    if (email && !groupForm.members.includes(email)) {
      if (/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
        setGroupForm({ ...groupForm, members: [...groupForm.members, email] });
        setNewEmail('');
      } else {
        Swal.fire('Invalid Email', 'Please enter a valid email address', 'error');
      }
    }
  };

  /* WHAT: Removes an email address from the recipient group's member list.
   * WHY: Allows admins to prune stale or incorrect addresses before saving the group. */
  const handleRemoveEmail = (email) => {
    setGroupForm({ ...groupForm, members: groupForm.members.filter(e => e !== email) });
  };

  /* WHAT: Creates or updates a recipient group via the backend API.
   * WHY: Persists group membership changes so routing rules can reference the group
   *      for future notification delivery. */
  const handleSaveGroup = async () => {
    if (!groupForm.group_code || !groupForm.group_name) {
      Swal.fire('Error', 'Group code and name are required', 'error');
      return;
    }

    setLoading(true);
    try {
      const url = editingGroup
        ? `notifications/groups/${selectedGroup.group_id}/`
        : 'notifications/groups/create/';
      const method = editingGroup ? 'PUT' : 'POST';

      const response = await fetch(API_CONFIG.getDatabasesEndpoint(url), {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(groupForm)
      });

      const data = await response.json();
      if (data.success) {
        Swal.fire('Success', editingGroup ? 'Group updated' : 'Group created', 'success');
        setShowGroupModal(false);
        loadRecipientGroups();
      } else {
        Swal.fire('Error', data.error, 'error');
      }
    } catch (err) {
      Swal.fire('Error', err.message, 'error');
    } finally {
      setLoading(false);
    }
  };

  /* WHAT: Deletes a recipient group after confirmation.
   * WHY: Stale groups should be removable; the confirmation dialog prevents
   *      accidental deletion of groups still referenced by routing rules. */
  const handleDeleteGroup = async (group) => {
    const result = await Swal.fire({
      title: 'Delete Group?',
      text: `Are you sure you want to delete "${group.group_name}"?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      confirmButtonText: 'Delete'
    });

    if (result.isConfirmed) {
      try {
        const response = await fetch(
          API_CONFIG.getDatabasesEndpoint(`notifications/groups/${group.group_id}/delete/`),
          { method: 'DELETE' }
        );
        const data = await response.json();
        if (data.success) {
          Swal.fire('Deleted', 'Group deleted successfully', 'success');
          loadRecipientGroups();
        } else {
          Swal.fire('Error', data.error, 'error');
        }
      } catch (err) {
        Swal.fire('Error', err.message, 'error');
      }
    }
  };

  // ============================================
  // ROUTING RULES CRUD
  // ============================================

  /* WHAT: Opens the routing rule creation modal with a blank form.
   * WHY: Routing rules connect notification types to recipient groups; this modal
   *      lets admins define new rules without navigating away from the tab. */
  const openRoutingModal = () => {
    setRoutingForm({
      type_code: '',
      technology_code: '',
      group_id: '',
      is_cc: false,
      priority: 0
    });
    setShowRoutingModal(true);
  };

  /* WHAT: Persists a new routing rule to the backend.
   * WHY: Each rule maps a notification type (optionally scoped to a technology)
   *      to a recipient group, controlling who receives that notification. */
  const handleSaveRoutingRule = async () => {
    if (!routingForm.type_code || !routingForm.group_id) {
      Swal.fire('Error', 'Notification type and recipient group are required', 'error');
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(API_CONFIG.getDatabasesEndpoint('notifications/routing/create/'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(routingForm)
      });

      const data = await response.json();
      if (data.success) {
        Swal.fire('Success', 'Routing rule created', 'success');
        setShowRoutingModal(false);
        loadRoutingRules();
      } else {
        Swal.fire('Error', data.error, 'error');
      }
    } catch (err) {
      Swal.fire('Error', err.message, 'error');
    } finally {
      setLoading(false);
    }
  };

  /* WHAT: Toggles a routing rule between active and inactive.
   * WHY: Admins can temporarily disable a rule (e.g., during an email migration)
   *      without deleting it and losing the configuration. */
  const handleToggleRoutingRule = async (rule) => {
    try {
      const response = await fetch(
        API_CONFIG.getDatabasesEndpoint(`notifications/routing/${rule.rule_id}/`),
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ is_active: !rule.is_active })
        }
      );
      const data = await response.json();
      if (data.success) {
        loadRoutingRules();
      } else {
        Swal.fire('Error', data.error, 'error');
      }
    } catch (err) {
      Swal.fire('Error', err.message, 'error');
    }
  };

  /* WHAT: Deletes a routing rule after user confirmation.
   * WHY: Permanently removes a notification-to-group mapping; the confirmation
   *      dialog prevents accidental removal of active delivery paths. */
  const handleDeleteRoutingRule = async (rule) => {
    const result = await Swal.fire({
      title: 'Delete Rule?',
      text: `Delete routing rule for ${rule.type_name} → ${rule.group_name}?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      confirmButtonText: 'Delete'
    });

    if (result.isConfirmed) {
      try {
        const response = await fetch(
          API_CONFIG.getDatabasesEndpoint(`notifications/routing/${rule.rule_id}/delete/`),
          { method: 'DELETE' }
        );
        const data = await response.json();
        if (data.success) {
          Swal.fire('Deleted', 'Routing rule deleted', 'success');
          loadRoutingRules();
        } else {
          Swal.fire('Error', data.error, 'error');
        }
      } catch (err) {
        Swal.fire('Error', err.message, 'error');
      }
    }
  };

  // ============================================
  // TEMPLATE CRUD
  // ============================================

  /* WHAT: Opens the template create/edit modal, pre-filling form data for edits.
   * WHY: Templates define the subject/body of each notification type; a single
   *      modal handles both create and edit to reduce UI duplication. */
  const openTemplateEditModal = (template = null) => {
    if (template) {
      setEditingTemplate(true);
      setSelectedTemplate(template);
      setTemplateForm({
        type_code: template.type_code,
        technology_code: template.technology_code || '',
        template_name: template.template_name,
        description: template.description || '',
        subject_template: template.subject_template,
        body_template_html: template.body_template_html || '',
        body_template_text: template.body_template_text || '',
        is_default: template.is_default
      });
    } else {
      setEditingTemplate(false);
      setSelectedTemplate(null);
      setTemplateForm({
        type_code: '',
        technology_code: '',
        template_name: '',
        description: '',
        subject_template: '',
        body_template_html: '',
        body_template_text: '',
        is_default: false
      });
    }
    setShowTemplateModal(true);
  };

  /* WHAT: Creates or updates an email template via the backend API.
   * WHY: Persists template changes so the notification engine uses the latest
   *      subject/body when sending emails for the associated notification type. */
  const handleSaveTemplate = async () => {
    if (!templateForm.type_code || !templateForm.template_name || !templateForm.subject_template || !templateForm.body_template_html) {
      Swal.fire('Error', 'Type, name, subject, and HTML body are required', 'error');
      return;
    }

    setLoading(true);
    try {
      const url = editingTemplate
        ? `notifications/templates/${selectedTemplate.template_id}/update/`
        : 'notifications/templates/create/';
      const method = editingTemplate ? 'PUT' : 'POST';

      const response = await fetch(API_CONFIG.getDatabasesEndpoint(url), {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(templateForm)
      });

      const data = await response.json();
      if (data.success) {
        Swal.fire('Success', editingTemplate ? 'Template updated' : 'Template created', 'success');
        setShowTemplateModal(false);
        loadTemplates();
      } else {
        Swal.fire('Error', data.error, 'error');
      }
    } catch (err) {
      Swal.fire('Error', err.message, 'error');
    } finally {
      setLoading(false);
    }
  };

  /* WHAT: Deletes an email template after user confirmation.
   * WHY: Removes obsolete templates; confirmation prevents accidental deletion
   *      of templates still in use by active notification types. */
  const handleDeleteTemplate = async (template) => {
    const result = await Swal.fire({
      title: 'Delete Template?',
      text: `Are you sure you want to delete "${template.template_name}"?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      confirmButtonText: 'Delete'
    });

    if (result.isConfirmed) {
      try {
        const response = await fetch(
          API_CONFIG.getDatabasesEndpoint(`notifications/templates/${template.template_id}/delete/`),
          { method: 'DELETE' }
        );
        const data = await response.json();
        if (data.success) {
          Swal.fire('Deleted', 'Template deleted successfully', 'success');
          loadTemplates();
        } else {
          Swal.fire('Error', data.error, 'error');
        }
      } catch (err) {
        Swal.fire('Error', err.message, 'error');
      }
    }
  };

  // ============================================
  // SETTINGS UPDATE
  // ============================================

  /* WHAT: Persists a single notification setting change to the backend.
   * WHY: Settings (sender address, throttle limits, etc.) are editable inline;
   *      each save call updates one key-value pair without a full form submit. */
  const handleUpdateSetting = async (settingKey, newValue) => {
    try {
      const response = await fetch(
        API_CONFIG.getDatabasesEndpoint(`notifications/settings/${settingKey}/`),
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ value: newValue })
        }
      );
      const data = await response.json();
      if (data.success) {
        Swal.fire('Success', 'Setting updated', 'success');
        setEditingSetting(null);
        loadSettings();
      } else {
        Swal.fire('Error', data.error, 'error');
      }
    } catch (err) {
      Swal.fire('Error', err.message, 'error');
    }
  };

  /* WHAT: Maps a notification priority level to its Bootstrap badge class.
   * WHY: Consistent color coding (red=CRITICAL, yellow=HIGH, blue=NORMAL)
   *      lets admins quickly scan priority in tables and queue views. */
  const getPriorityBadgeClass = (priority) => {
    switch (priority) {
      case 'CRITICAL': return 'bg-danger';
      case 'HIGH': return 'bg-warning text-dark';
      case 'NORMAL': return 'bg-info';
      case 'LOW': return 'bg-secondary';
      default: return 'bg-secondary';
    }
  };

  /* WHAT: Maps a notification delivery status to its Bootstrap badge class.
   * WHY: Visual status indicators (green=SENT, red=FAILED, yellow=PENDING)
   *      make the history and queue tables scannable at a glance. */
  const getStatusBadgeClass = (status) => {
    switch (status) {
      case 'SENT': return 'bg-success';
      case 'FAILED': return 'bg-danger';
      case 'PENDING': return 'bg-warning text-dark';
      case 'PROCESSING': return 'bg-info';
      default: return 'bg-secondary';
    }
  };

  return (
    <div className="container-fluid">
      <div className="row">
        <div className="col-12">
          {/* Header */}
          <div className="d-flex justify-content-between align-items-center mb-4">
            <h2>
              <i className="fas fa-bell text-primary me-2"></i>
              Notification Manager
            </h2>
            <button
              className="btn btn-outline-primary"
              onClick={() => {
                loadDashboardStats();
                if (activeTab === 'history') loadHistory();
                if (activeTab === 'queue') loadQueue();
              }}
            >
              <i className="fas fa-sync-alt me-2"></i>
              Refresh
            </button>
          </div>

          {/* Tabs */}
          <ul className="nav nav-tabs mb-4">
            <li className="nav-item">
              <button
                className={`nav-link ${activeTab === 'dashboard' ? 'active' : ''}`}
                onClick={() => setActiveTab('dashboard')}
              >
                <i className="fas fa-chart-line me-1"></i> Dashboard
              </button>
            </li>
            <li className="nav-item">
              <button
                className={`nav-link ${activeTab === 'types' ? 'active' : ''}`}
                onClick={() => setActiveTab('types')}
              >
                <i className="fas fa-list me-1"></i> Types
              </button>
            </li>
            <li className="nav-item">
              <button
                className={`nav-link ${activeTab === 'templates' ? 'active' : ''}`}
                onClick={() => setActiveTab('templates')}
              >
                <i className="fas fa-file-alt me-1"></i> Templates
              </button>
            </li>
            <li className="nav-item">
              <button
                className={`nav-link ${activeTab === 'groups' ? 'active' : ''}`}
                onClick={() => setActiveTab('groups')}
              >
                <i className="fas fa-users me-1"></i> Recipients
              </button>
            </li>
            <li className="nav-item">
              <button
                className={`nav-link ${activeTab === 'routing' ? 'active' : ''}`}
                onClick={() => setActiveTab('routing')}
              >
                <i className="fas fa-route me-1"></i> Routing
              </button>
            </li>
            <li className="nav-item">
              <button
                className={`nav-link ${activeTab === 'queue' ? 'active' : ''}`}
                onClick={() => setActiveTab('queue')}
              >
                <i className="fas fa-inbox me-1"></i> Queue
                {queueStats.PENDING > 0 && (
                  <span className="badge bg-warning text-dark ms-1">{queueStats.PENDING}</span>
                )}
              </button>
            </li>
            <li className="nav-item">
              <button
                className={`nav-link ${activeTab === 'history' ? 'active' : ''}`}
                onClick={() => setActiveTab('history')}
              >
                <i className="fas fa-history me-1"></i> History
              </button>
            </li>
            <li className="nav-item">
              <button
                className={`nav-link ${activeTab === 'settings' ? 'active' : ''}`}
                onClick={() => setActiveTab('settings')}
              >
                <i className="fas fa-cog me-1"></i> Settings
              </button>
            </li>
            <li className="nav-item">
              <button
                className={`nav-link ${activeTab === 'test' ? 'active' : ''}`}
                onClick={() => setActiveTab('test')}
              >
                <i className="fas fa-vial me-1"></i> Test
              </button>
            </li>
          </ul>

          {/* Dashboard Tab */}
          {activeTab === 'dashboard' && (
            <div>
              {loadingDashboard ? (
                <div className="text-center py-5">
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                </div>
              ) : dashboardStats && (
                <>
                  {/* Stats Cards */}
                  <div className="row mb-4">
                    <div className="col-md-3">
                      <div className="card border-0 shadow-sm" style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
                        <div className="card-body text-white">
                          <div className="d-flex justify-content-between align-items-center">
                            <div>
                              <h6 className="mb-1" style={{ opacity: 0.9 }}>Queue Pending</h6>
                              <h2 className="mb-0">{dashboardStats.queue?.pending || 0}</h2>
                            </div>
                            <i className="fas fa-inbox fa-2x" style={{ opacity: 0.8 }}></i>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="col-md-3">
                      <div className="card border-0 shadow-sm" style={{ background: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)' }}>
                        <div className="card-body text-white">
                          <div className="d-flex justify-content-between align-items-center">
                            <div>
                              <h6 className="mb-1" style={{ opacity: 0.9 }}>Sent (24h)</h6>
                              <h2 className="mb-0">{dashboardStats.history_24h?.sent || 0}</h2>
                            </div>
                            <i className="fas fa-paper-plane fa-2x" style={{ opacity: 0.8 }}></i>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="col-md-3">
                      <div className="card border-0 shadow-sm" style={{ background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)' }}>
                        <div className="card-body text-white">
                          <div className="d-flex justify-content-between align-items-center">
                            <div>
                              <h6 className="mb-1" style={{ opacity: 0.9 }}>Failed (24h)</h6>
                              <h2 className="mb-0">{dashboardStats.history_24h?.failed || 0}</h2>
                            </div>
                            <i className="fas fa-exclamation-triangle fa-2x" style={{ opacity: 0.8 }}></i>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="col-md-3">
                      <div className="card border-0 shadow-sm" style={{ background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)' }}>
                        <div className="card-body text-white">
                          <div className="d-flex justify-content-between align-items-center">
                            <div>
                              <h6 className="mb-1" style={{ opacity: 0.9 }}>Sent (7d)</h6>
                              <h2 className="mb-0">{dashboardStats.history_7d?.sent || 0}</h2>
                            </div>
                            <i className="fas fa-chart-line fa-2x" style={{ opacity: 0.8 }}></i>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="row">
                    {/* By Type */}
                    <div className="col-md-6">
                      <div className="card shadow-sm mb-4">
                        <div className="card-header bg-white">
                          <h6 className="mb-0">
                            <i className="fas fa-chart-bar text-primary me-2"></i>
                            Notifications by Type (7 days)
                          </h6>
                        </div>
                        <div className="card-body p-0">
                          <table className="table table-hover mb-0">
                            <thead className="table-dark">
                              <tr>
                                <th>Type</th>
                                <th className="text-center">Total</th>
                                <th className="text-center">Sent</th>
                                <th className="text-center">Failed</th>
                              </tr>
                            </thead>
                            <tbody>
                              {dashboardStats.by_type?.map((item, idx) => (
                                <tr key={idx}>
                                  <td>{item.type_name}</td>
                                  <td className="text-center">{item.total}</td>
                                  <td className="text-center">
                                    <span className="badge bg-success">{item.sent}</span>
                                  </td>
                                  <td className="text-center">
                                    {item.failed > 0 ? (
                                      <span className="badge bg-danger">{item.failed}</span>
                                    ) : '-'}
                                  </td>
                                </tr>
                              ))}
                              {(!dashboardStats.by_type || dashboardStats.by_type.length === 0) && (
                                <tr>
                                  <td colSpan="4" className="text-center text-muted py-3">
                                    No notifications in the last 7 days
                                  </td>
                                </tr>
                              )}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </div>

                    {/* Recent Failures */}
                    <div className="col-md-6">
                      <div className="card shadow-sm mb-4">
                        <div className="card-header bg-white">
                          <h6 className="mb-0">
                            <i className="fas fa-exclamation-circle text-danger me-2"></i>
                            Recent Failures (24h)
                          </h6>
                        </div>
                        <div className="card-body p-0">
                          {dashboardStats.recent_failures?.length > 0 ? (
                            <div className="list-group list-group-flush">
                              {dashboardStats.recent_failures.map((item, idx) => (
                                <div key={idx} className="list-group-item">
                                  <div className="d-flex justify-content-between">
                                    <strong>{item.type_name}</strong>
                                    <small className="text-muted">
                                      {new Date(item.created_at).toLocaleString()}
                                    </small>
                                  </div>
                                  <small className="text-muted">{item.subject}</small>
                                  <div className="text-danger small mt-1">
                                    <i className="fas fa-exclamation-triangle me-1"></i>
                                    {item.error_message}
                                  </div>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <div className="text-center text-muted py-4">
                              <i className="fas fa-check-circle fa-2x text-success mb-2"></i>
                              <p className="mb-0">No failures in the last 24 hours</p>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </>
              )}
            </div>
          )}

          {/* Types Tab */}
          {activeTab === 'types' && (
            <div>
              {/* Category Filter */}
              <div className="mb-3">
                <div className="btn-group">
                  <button
                    className={`btn ${selectedCategory === '' ? 'btn-primary' : 'btn-outline-primary'}`}
                    onClick={() => setSelectedCategory('')}
                  >
                    All
                  </button>
                  {typeCategories.map(cat => (
                    <button
                      key={cat.name}
                      className={`btn ${selectedCategory === cat.name ? 'btn-primary' : 'btn-outline-primary'}`}
                      onClick={() => setSelectedCategory(cat.name)}
                    >
                      {cat.name} ({cat.count})
                    </button>
                  ))}
                </div>
              </div>

              {/* Types Table */}
              <div className="card shadow-sm">
                <div className="card-body p-0">
                  <table className="table table-hover mb-0">
                    <thead className="table-dark">
                      <tr>
                        <th>Type</th>
                        <th>Category</th>
                        <th className="text-center">Priority</th>
                        <th className="text-center">Tech Routing</th>
                        <th className="text-center">Templates</th>
                        <th className="text-center">Rules</th>
                        <th className="text-center">Enabled</th>
                      </tr>
                    </thead>
                    <tbody>
                      {notificationTypes.map(type => (
                        <tr key={type.type_code}>
                          <td>
                            <strong>{type.type_name}</strong>
                            {type.is_system && (
                              <span className="badge bg-info ms-2">System</span>
                            )}
                            <br />
                            <small className="text-muted">{type.type_code}</small>
                          </td>
                          <td>
                            <span className="badge bg-secondary">{type.category}</span>
                          </td>
                          <td className="text-center">
                            <span className={`badge ${getPriorityBadgeClass(type.default_priority)}`}>
                              {type.default_priority}
                            </span>
                          </td>
                          <td className="text-center">
                            {type.supports_technology_routing ? (
                              <i className="fas fa-check text-success"></i>
                            ) : (
                              <i className="fas fa-times text-muted"></i>
                            )}
                          </td>
                          <td className="text-center">
                            <span className="badge bg-primary">{type.template_count}</span>
                          </td>
                          <td className="text-center">
                            <span className="badge bg-info">{type.routing_rule_count}</span>
                          </td>
                          <td className="text-center">
                            <div className="form-check form-switch d-inline-block">
                              <input
                                type="checkbox"
                                className="form-check-input"
                                checked={type.default_enabled}
                                onChange={() => handleToggleType(type.type_code, type.default_enabled)}
                                disabled={type.is_system}
                              />
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {/* Templates Tab */}
          {activeTab === 'templates' && (
            <div>
              <div className="d-flex justify-content-end mb-3">
                <button className="btn btn-primary" onClick={() => openTemplateEditModal()}>
                  <i className="fas fa-plus me-2"></i>Create Template
                </button>
              </div>
              <div className="card shadow-sm">
                <div className="card-body p-0">
                  <table className="table table-hover mb-0">
                    <thead className="table-dark">
                      <tr>
                        <th>Template</th>
                        <th>Type</th>
                        <th>Technology</th>
                        <th className="text-center">Default</th>
                        <th className="text-center">Version</th>
                        <th className="text-center">Active</th>
                        <th className="text-center">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {templates.map(template => (
                        <tr key={template.template_id}>
                          <td>
                            <strong>{template.template_name}</strong>
                            <br />
                            <small className="text-muted">{template.subject_template?.substring(0, 50)}...</small>
                          </td>
                          <td>{template.type_name}</td>
                          <td>{template.technology_name || <span className="text-muted">Generic</span>}</td>
                          <td className="text-center">
                            {template.is_default && <i className="fas fa-star text-warning"></i>}
                          </td>
                          <td className="text-center">v{template.version}</td>
                          <td className="text-center">
                            {template.is_active ? (
                              <i className="fas fa-check text-success"></i>
                            ) : (
                              <i className="fas fa-times text-danger"></i>
                            )}
                          </td>
                          <td className="text-center">
                            <button
                              className="btn btn-sm btn-outline-primary me-1"
                              onClick={() => openTemplateEditModal(template)}
                              title="Edit"
                            >
                              <i className="fas fa-edit"></i>
                            </button>
                            <button
                              className="btn btn-sm btn-outline-danger"
                              onClick={() => handleDeleteTemplate(template)}
                              title="Delete"
                            >
                              <i className="fas fa-trash"></i>
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {/* Recipient Groups Tab */}
          {activeTab === 'groups' && (
            <div>
              <div className="d-flex justify-content-end mb-3">
                <button className="btn btn-primary" onClick={() => openGroupModal()}>
                  <i className="fas fa-plus me-2"></i>Create Group
                </button>
              </div>
              <div className="card shadow-sm">
                <div className="card-body p-0">
                  <table className="table table-hover mb-0">
                    <thead className="table-dark">
                      <tr>
                        <th>Group</th>
                        <th>Technology</th>
                        <th>Email Members</th>
                        <th className="text-center">Roles</th>
                        <th className="text-center">Rules</th>
                        <th className="text-center">Active</th>
                        <th className="text-center">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {recipientGroups.map(group => (
                        <tr key={group.group_id}>
                          <td>
                            <strong>{group.group_name}</strong>
                            <br />
                            <small className="text-muted">{group.group_code}</small>
                          </td>
                          <td>{group.technology_name || <span className="text-muted">-</span>}</td>
                          <td>
                            <span className="badge bg-primary me-1">{group.member_count} emails</span>
                            {(() => {
                              const members = Array.isArray(group.members) ? group.members :
                                (typeof group.members === 'string' ? JSON.parse(group.members || '[]') : []);
                              return members.length > 0 && (
                                <small className="text-muted d-block mt-1">
                                  {members.slice(0, 2).join(', ')}
                                  {members.length > 2 && ` +${members.length - 2} more`}
                                </small>
                              );
                            })()}
                          </td>
                          <td className="text-center">
                            <span className="badge bg-info">{group.role_count}</span>
                          </td>
                          <td className="text-center">
                            <span className="badge bg-secondary">{group.rule_count}</span>
                          </td>
                          <td className="text-center">
                            {group.is_active ? (
                              <i className="fas fa-check text-success"></i>
                            ) : (
                              <i className="fas fa-times text-danger"></i>
                            )}
                          </td>
                          <td className="text-center">
                            <button
                              className="btn btn-sm btn-outline-primary me-1"
                              onClick={() => openGroupModal(group)}
                              title="Edit"
                            >
                              <i className="fas fa-edit"></i>
                            </button>
                            <button
                              className="btn btn-sm btn-outline-danger"
                              onClick={() => handleDeleteGroup(group)}
                              title="Delete"
                              disabled={group.rule_count > 0}
                            >
                              <i className="fas fa-trash"></i>
                            </button>
                          </td>
                        </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
          )}

          {/* Routing Tab */}
          {activeTab === 'routing' && (
            <div>
              <div className="d-flex justify-content-between align-items-start mb-3">
                <div className="alert alert-info mb-0 py-2">
                  <i className="fas fa-info-circle me-2"></i>
                  Routes notifications to different teams based on technology (e.g., Oracle alerts to Oracle DBAs)
                </div>
                <button className="btn btn-primary ms-3" onClick={openRoutingModal}>
                  <i className="fas fa-plus me-2"></i>Add Rule
                </button>
              </div>
              <div className="card shadow-sm">
                <div className="card-body p-0">
                  <table className="table table-hover mb-0">
                    <thead className="table-dark">
                      <tr>
                        <th>Notification Type</th>
                        <th>Technology</th>
                        <th>Recipient Group</th>
                        <th className="text-center">CC</th>
                        <th className="text-center">Priority</th>
                        <th className="text-center">Active</th>
                        <th className="text-center">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {routingRules.map(rule => (
                        <tr key={rule.rule_id}>
                          <td>{rule.type_name}</td>
                          <td>
                            {rule.technology_code ? (
                              <span className="badge bg-primary">{rule.technology_name}</span>
                            ) : (
                              <span className="badge bg-secondary">(Default)</span>
                            )}
                          </td>
                          <td>{rule.group_name}</td>
                          <td className="text-center">
                            {rule.is_cc && <i className="fas fa-check text-info"></i>}
                          </td>
                          <td className="text-center">{rule.priority}</td>
                          <td className="text-center">
                            <div className="form-check form-switch d-inline-block">
                              <input
                                type="checkbox"
                                className="form-check-input"
                                checked={rule.is_active}
                                onChange={() => handleToggleRoutingRule(rule)}
                              />
                            </div>
                          </td>
                          <td className="text-center">
                            <button
                              className="btn btn-sm btn-outline-danger"
                              onClick={() => handleDeleteRoutingRule(rule)}
                              title="Delete"
                            >
                              <i className="fas fa-trash"></i>
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {/* Queue Tab */}
          {activeTab === 'queue' && (
            <div>
              <div className="d-flex justify-content-between mb-3">
                <div>
                  <span className="badge bg-warning text-dark me-2">
                    Pending: {queueStats.PENDING || 0}
                  </span>
                  <span className="badge bg-info me-2">
                    Processing: {queueStats.PROCESSING || 0}
                  </span>
                  <span className="badge bg-danger">
                    Failed: {queueStats.FAILED || 0}
                  </span>
                </div>
                <button
                  className="btn btn-primary"
                  onClick={handleProcessQueue}
                  disabled={loading}
                >
                  {loading ? (
                    <span className="spinner-border spinner-border-sm me-2"></span>
                  ) : (
                    <i className="fas fa-play me-2"></i>
                  )}
                  Process Queue
                </button>
              </div>

              <div className="card shadow-sm">
                <div className="card-body p-0">
                  <table className="table table-hover mb-0">
                    <thead className="table-dark">
                      <tr>
                        <th>Subject</th>
                        <th>Type</th>
                        <th>Technology</th>
                        <th className="text-center">Priority</th>
                        <th className="text-center">Status</th>
                        <th className="text-center">Retries</th>
                        <th>Scheduled</th>
                      </tr>
                    </thead>
                    <tbody>
                      {queue.map(item => (
                        <tr key={item.queue_id}>
                          <td>
                            <strong>{item.subject?.substring(0, 40)}...</strong>
                            <br />
                            <small className="text-muted">
                              {Array.isArray(item.recipients_to)
                                ? item.recipients_to.slice(0, 2).join(', ')
                                : item.recipients_to}
                            </small>
                          </td>
                          <td>{item.type_name}</td>
                          <td>{item.technology_name || '-'}</td>
                          <td className="text-center">
                            <span className={`badge ${getPriorityBadgeClass(item.priority)}`}>
                              {item.priority}
                            </span>
                          </td>
                          <td className="text-center">
                            <span className={`badge ${getStatusBadgeClass(item.status)}`}>
                              {item.status}
                            </span>
                          </td>
                          <td className="text-center">
                            {item.retry_count}/{item.max_retries}
                          </td>
                          <td>
                            <small>{item.scheduled_at ? new Date(item.scheduled_at).toLocaleString() : '-'}</small>
                          </td>
                        </tr>
                      ))}
                      {queue.length === 0 && (
                        <tr>
                          <td colSpan="7" className="text-center py-4 text-muted">
                            <i className="fas fa-check-circle fa-2x text-success mb-2 d-block"></i>
                            Queue is empty
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {/* History Tab */}
          {activeTab === 'history' && (
            <div>
              <div className="d-flex justify-content-between mb-3">
                <div>
                  <span className="badge bg-success me-2">
                    Sent: {historyStats.sent || 0}
                  </span>
                  <span className="badge bg-danger">
                    Failed: {historyStats.failed || 0}
                  </span>
                </div>
                <select
                  className="form-select w-auto"
                  value={historyDays}
                  onChange={(e) => setHistoryDays(parseInt(e.target.value))}
                >
                  <option value="1">Last 24 hours</option>
                  <option value="7">Last 7 days</option>
                  <option value="30">Last 30 days</option>
                </select>
              </div>

              <div className="card shadow-sm">
                <div className="card-body p-0">
                  <table className="table table-hover mb-0">
                    <thead className="table-dark">
                      <tr>
                        <th>Subject</th>
                        <th>Type</th>
                        <th>Technology</th>
                        <th className="text-center">Status</th>
                        <th>Sent</th>
                        <th>Triggered By</th>
                      </tr>
                    </thead>
                    <tbody>
                      {history.map(item => (
                        <tr key={item.history_id}>
                          <td>
                            <strong>{item.subject?.substring(0, 40)}...</strong>
                            {item.error_message && (
                              <div className="text-danger small">
                                <i className="fas fa-exclamation-triangle me-1"></i>
                                {item.error_message.substring(0, 50)}...
                              </div>
                            )}
                          </td>
                          <td>{item.type_name}</td>
                          <td>{item.technology_name || '-'}</td>
                          <td className="text-center">
                            <span className={`badge ${getStatusBadgeClass(item.status)}`}>
                              {item.status}
                            </span>
                          </td>
                          <td>
                            <small>{item.sent_at ? new Date(item.sent_at).toLocaleString() : '-'}</small>
                          </td>
                          <td>
                            <small>{item.triggered_by}</small>
                          </td>
                        </tr>
                      ))}
                      {history.length === 0 && (
                        <tr>
                          <td colSpan="6" className="text-center py-4 text-muted">
                            No notifications in the selected period
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {/* Settings Tab */}
          {activeTab === 'settings' && (
            <div className="card shadow-sm">
              <div className="card-body p-0">
                <table className="table table-hover mb-0">
                  <thead className="table-dark">
                    <tr>
                      <th>Setting</th>
                      <th>Value</th>
                      <th>Category</th>
                      <th>Description</th>
                    </tr>
                  </thead>
                  <tbody>
                    {settings.map(setting => (
                      <tr key={setting.setting_key}>
                        <td>
                          <code>{setting.setting_key}</code>
                        </td>
                        <td>
                          {setting.is_sensitive ? (
                            <span className="text-muted">********</span>
                          ) : (
                            <strong>{setting.setting_value}</strong>
                          )}
                        </td>
                        <td>
                          <span className="badge bg-secondary">{setting.category}</span>
                        </td>
                        <td>
                          <small className="text-muted">{setting.description}</small>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Test Tab */}
          {activeTab === 'test' && (
            <div className="row">
              <div className="col-md-6">
                <div className="card shadow-sm">
                  <div className="card-header bg-white">
                    <h6 className="mb-0">
                      <i className="fas fa-vial text-primary me-2"></i>
                      Send Test Notification
                    </h6>
                  </div>
                  <div className="card-body">
                    <form onSubmit={handleSendTest}>
                      <div className="mb-3">
                        <label className="form-label">Notification Type *</label>
                        <select
                          className="form-select"
                          value={testForm.type_code}
                          onChange={(e) => setTestForm({ ...testForm, type_code: e.target.value })}
                          required
                        >
                          <option value="">Select type...</option>
                          {notificationTypes.map(type => (
                            <option key={type.type_code} value={type.type_code}>
                              {type.type_name}
                            </option>
                          ))}
                        </select>
                      </div>

                      <div className="mb-3">
                        <label className="form-label">Technology (for routing)</label>
                        <select
                          className="form-select"
                          value={testForm.technology_code}
                          onChange={(e) => setTestForm({ ...testForm, technology_code: e.target.value })}
                        >
                          <option value="">None (use defaults)</option>
                          {technologies.filter(t => t.is_active).map(tech => (
                            <option key={tech.technology_code} value={tech.technology_code}>
                              {tech.display_name} ({tech.system_type})
                            </option>
                          ))}
                        </select>
                        <small className="text-muted">
                          Select a technology to test technology-based routing
                        </small>
                      </div>

                      <div className="mb-3">
                        <label className="form-label">Recipient Email *</label>
                        <input
                          type="email"
                          className="form-control"
                          value={testForm.recipient_email}
                          onChange={(e) => setTestForm({ ...testForm, recipient_email: e.target.value })}
                          placeholder="test@example.com"
                          required
                        />
                      </div>

                      <button
                        type="submit"
                        className="btn btn-primary"
                        disabled={sendingTest}
                      >
                        {sendingTest ? (
                          <>
                            <span className="spinner-border spinner-border-sm me-2"></span>
                            Sending...
                          </>
                        ) : (
                          <>
                            <i className="fas fa-paper-plane me-2"></i>
                            Send Test
                          </>
                        )}
                      </button>
                    </form>
                  </div>
                </div>
              </div>

              <div className="col-md-6">
                <div className="card shadow-sm">
                  <div className="card-header bg-white">
                    <h6 className="mb-0">
                      <i className="fas fa-info-circle text-info me-2"></i>
                      Test Information
                    </h6>
                  </div>
                  <div className="card-body">
                    <p>Test notifications are sent immediately (not queued) with sample data.</p>

                    <h6>Available Test Types:</h6>
                    <ul>
                      <li><strong>JOB_FAILURE</strong> - Simulates a job failure with sample error</li>
                      <li><strong>ATTESTATION_REMINDER</strong> - Sample attestation reminder</li>
                      <li><strong>CAMPAIGN_COMPLETED</strong> - Sample campaign completion</li>
                    </ul>

                    <div className="alert alert-info mb-0">
                      <i className="fas fa-lightbulb me-2"></i>
                      <strong>Tip:</strong> Select a technology to test routing rules.
                      For example, selecting "Oracle" for JOB_FAILURE will route to the Oracle DBA team.
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Template Edit Modal */}
      <WideModal
        show={showTemplateModal}
        onClose={() => {
          setShowTemplateModal(false);
          setSelectedTemplate(null);
          setEditingTemplate(false);
        }}
        title={editingTemplate ? `Edit: ${selectedTemplate?.template_name}` : 'Create New Template'}
        headerIcon="fa-file-alt"
        footer={
          <>
            <button
              className="btn btn-secondary"
              onClick={() => {
                setShowTemplateModal(false);
                setSelectedTemplate(null);
                setEditingTemplate(false);
              }}
            >
              Cancel
            </button>
            <button
              className="btn btn-primary"
              onClick={handleSaveTemplate}
              disabled={loading}
            >
              {loading ? (
                <><span className="spinner-border spinner-border-sm me-2"></span>Saving...</>
              ) : (
                <><i className="fas fa-save me-2"></i>{editingTemplate ? 'Update Template' : 'Create Template'}</>
              )}
            </button>
          </>
        }
      >
        <div className="row">
          <div className="col-md-6">
            <div className="mb-3">
              <label className="form-label">Notification Type *</label>
              <select
                className="form-select"
                value={templateForm.type_code}
                onChange={(e) => setTemplateForm({ ...templateForm, type_code: e.target.value })}
                disabled={editingTemplate}
              >
                <option value="">Select type...</option>
                {notificationTypes.map(type => (
                  <option key={type.type_code} value={type.type_code}>{type.type_name}</option>
                ))}
              </select>
            </div>
            <div className="mb-3">
              <label className="form-label">Template Name *</label>
              <input
                type="text"
                className="form-control"
                value={templateForm.template_name}
                onChange={(e) => setTemplateForm({ ...templateForm, template_name: e.target.value })}
                placeholder="e.g., Job Failure - Oracle Specific"
              />
            </div>
            <div className="mb-3">
              <label className="form-label">Technology (Optional)</label>
              <select
                className="form-select"
                value={templateForm.technology_code}
                onChange={(e) => setTemplateForm({ ...templateForm, technology_code: e.target.value })}
              >
                <option value="">Generic (all technologies)</option>
                {technologies.filter(t => t.is_active).map(tech => (
                  <option key={tech.technology_code} value={tech.technology_code}>{tech.display_name}</option>
                ))}
              </select>
            </div>
          </div>
          <div className="col-md-6">
            <div className="mb-3">
              <label className="form-label">Description</label>
              <input
                type="text"
                className="form-control"
                value={templateForm.description}
                onChange={(e) => setTemplateForm({ ...templateForm, description: e.target.value })}
                placeholder="Brief description of this template"
              />
            </div>
            <div className="mb-3">
              <label className="form-label">Subject Line Template *</label>
              <input
                type="text"
                className="form-control"
                value={templateForm.subject_template}
                onChange={(e) => setTemplateForm({ ...templateForm, subject_template: e.target.value })}
                placeholder="e.g., [DAMA] Job Failure: {{job_name}}"
              />
              <small className="text-muted">Use {'{{variable_name}}'} for placeholders</small>
            </div>
            <div className="form-check mb-3">
              <input
                type="checkbox"
                className="form-check-input"
                id="isDefaultTemplate"
                checked={templateForm.is_default}
                onChange={(e) => setTemplateForm({ ...templateForm, is_default: e.target.checked })}
              />
              <label className="form-check-label" htmlFor="isDefaultTemplate">
                Set as default template for this type/technology
              </label>
            </div>
          </div>
        </div>
        <div className="mb-3">
          <label className="form-label">HTML Body Template *</label>
          <textarea
            className="form-control font-monospace"
            rows="12"
            value={templateForm.body_template_html}
            onChange={(e) => setTemplateForm({ ...templateForm, body_template_html: e.target.value })}
            placeholder="<html><body>Your HTML email template...</body></html>"
            style={{ fontSize: '12px' }}
          />
        </div>
        <div className="mb-3">
          <label className="form-label">Plain Text Body Template (Optional)</label>
          <textarea
            className="form-control font-monospace"
            rows="6"
            value={templateForm.body_template_text}
            onChange={(e) => setTemplateForm({ ...templateForm, body_template_text: e.target.value })}
            placeholder="Plain text version of the email..."
            style={{ fontSize: '12px' }}
          />
        </div>
      </WideModal>

      {/* Recipient Group Edit Modal */}
      <WideModal
        show={showGroupModal}
        onClose={() => setShowGroupModal(false)}
        title={editingGroup ? `Edit: ${selectedGroup?.group_name}` : 'Create Recipient Group'}
        headerIcon="fa-users"
        footer={
          <>
            <button className="btn btn-secondary" onClick={() => setShowGroupModal(false)}>Cancel</button>
            <button className="btn btn-primary" onClick={handleSaveGroup} disabled={loading}>
              {loading ? (
                <><span className="spinner-border spinner-border-sm me-2"></span>Saving...</>
              ) : (
                <><i className="fas fa-save me-2"></i>{editingGroup ? 'Update Group' : 'Create Group'}</>
              )}
            </button>
          </>
        }
      >
        <div className="row">
          <div className="col-md-6">
            <div className="mb-3">
              <label className="form-label">Group Code *</label>
              <input
                type="text"
                className="form-control"
                value={groupForm.group_code}
                onChange={(e) => setGroupForm({ ...groupForm, group_code: e.target.value.toUpperCase().replace(/[^A-Z0-9_]/g, '') })}
                placeholder="e.g., DBA_ORACLE_TEAM"
                disabled={editingGroup}
              />
              <small className="text-muted">Uppercase letters, numbers, and underscores only</small>
            </div>
            <div className="mb-3">
              <label className="form-label">Group Name *</label>
              <input
                type="text"
                className="form-control"
                value={groupForm.group_name}
                onChange={(e) => setGroupForm({ ...groupForm, group_name: e.target.value })}
                placeholder="e.g., Oracle DBA Team"
              />
            </div>
            <div className="mb-3">
              <label className="form-label">Description</label>
              <textarea
                className="form-control"
                rows="2"
                value={groupForm.description}
                onChange={(e) => setGroupForm({ ...groupForm, description: e.target.value })}
                placeholder="Describe this group..."
              />
            </div>
            <div className="mb-3">
              <label className="form-label">Technology Association</label>
              <select
                className="form-select"
                value={groupForm.technology_code}
                onChange={(e) => setGroupForm({ ...groupForm, technology_code: e.target.value })}
              >
                <option value="">None (General group)</option>
                {technologies.filter(t => t.is_active).map(tech => (
                  <option key={tech.technology_code} value={tech.technology_code}>{tech.display_name}</option>
                ))}
              </select>
            </div>
          </div>
          <div className="col-md-6">
            <div className="mb-3">
              <label className="form-label">
                Email Members
                <span className="badge bg-primary ms-2">{groupForm.members.length}</span>
              </label>
              <div className="input-group mb-2">
                <input
                  type="email"
                  className="form-control"
                  value={newEmail}
                  onChange={(e) => setNewEmail(e.target.value)}
                  placeholder="email@example.com"
                  onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddEmail())}
                />
                <button className="btn btn-outline-primary" type="button" onClick={handleAddEmail}>
                  <i className="fas fa-plus"></i>
                </button>
              </div>
              <div style={{ maxHeight: '200px', overflowY: 'auto' }}>
                {groupForm.members.map((email, idx) => (
                  <span key={idx} className="badge bg-secondary me-1 mb-1" style={{ fontSize: '0.9em' }}>
                    {email}
                    <button
                      type="button"
                      className="btn-close btn-close-white ms-2"
                      style={{ fontSize: '0.6em' }}
                      onClick={() => handleRemoveEmail(email)}
                    ></button>
                  </span>
                ))}
                {groupForm.members.length === 0 && (
                  <div className="text-muted small">No email members added yet</div>
                )}
              </div>
            </div>
          </div>
        </div>
      </WideModal>

      {/* Routing Rule Create Modal */}
      <WideModal
        show={showRoutingModal}
        onClose={() => setShowRoutingModal(false)}
        title="Add Routing Rule"
        headerIcon="fa-route"
        maxWidth="700px"
        footer={
          <>
            <button className="btn btn-secondary" onClick={() => setShowRoutingModal(false)}>Cancel</button>
            <button className="btn btn-primary" onClick={handleSaveRoutingRule} disabled={loading}>
              {loading ? (
                <><span className="spinner-border spinner-border-sm me-2"></span>Creating...</>
              ) : (
                <><i className="fas fa-plus me-2"></i>Create Rule</>
              )}
            </button>
          </>
        }
      >
        <div className="mb-3">
          <label className="form-label">Notification Type *</label>
          <select
            className="form-select"
            value={routingForm.type_code}
            onChange={(e) => setRoutingForm({ ...routingForm, type_code: e.target.value })}
          >
            <option value="">Select notification type...</option>
            {notificationTypes.filter(t => t.supports_technology_routing).map(type => (
              <option key={type.type_code} value={type.type_code}>{type.type_name}</option>
            ))}
          </select>
        </div>
        <div className="mb-3">
          <label className="form-label">Technology (Optional)</label>
          <select
            className="form-select"
            value={routingForm.technology_code}
            onChange={(e) => setRoutingForm({ ...routingForm, technology_code: e.target.value })}
          >
            <option value="">Default (all technologies)</option>
            {technologies.filter(t => t.is_active).map(tech => (
              <option key={tech.technology_code} value={tech.technology_code}>{tech.display_name}</option>
            ))}
          </select>
          <small className="text-muted">Leave empty for default routing, or select a technology for specific routing</small>
        </div>
        <div className="mb-3">
          <label className="form-label">Recipient Group *</label>
          <select
            className="form-select"
            value={routingForm.group_id}
            onChange={(e) => setRoutingForm({ ...routingForm, group_id: e.target.value })}
          >
            <option value="">Select recipient group...</option>
            {recipientGroups.filter(g => g.is_active).map(group => (
              <option key={group.group_id} value={group.group_id}>
                {group.group_name} ({group.member_count} members)
              </option>
            ))}
          </select>
        </div>
        <div className="row">
          <div className="col-md-6">
            <div className="mb-3">
              <label className="form-label">Priority</label>
              <input
                type="number"
                className="form-control"
                value={routingForm.priority}
                onChange={(e) => setRoutingForm({ ...routingForm, priority: parseInt(e.target.value) || 0 })}
                min="0"
                max="100"
              />
              <small className="text-muted">Lower numbers = higher priority</small>
            </div>
          </div>
          <div className="col-md-6">
            <div className="mb-3">
              <label className="form-label">&nbsp;</label>
              <div className="form-check mt-2">
                <input
                  type="checkbox"
                  className="form-check-input"
                  id="routingIsCC"
                  checked={routingForm.is_cc}
                  onChange={(e) => setRoutingForm({ ...routingForm, is_cc: e.target.checked })}
                />
                <label className="form-check-label" htmlFor="routingIsCC">
                  CC recipients (don't use as primary)
                </label>
              </div>
            </div>
          </div>
        </div>
      </WideModal>
    </div>
  );
};

export default NotificationManager;
